import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ISelectionObserver } from '@app/interfaces/selectionObserver.interface';
import { AssetHistoryActionType } from '@app/_models/assetHistoryActionType';
import { AssetType } from '@app/_models/enums/assetType';
import { SortDirection } from '@app/_models/enums/sortDirection';
import { Sort } from '@app/_models/sort';
import { ITuple } from '@app/_models/tuple';
import { Asset, AssetTypeName } from '@app/_models/_DTOs/asset';
import { AssetHistoryActionName } from '@app/_models/_DTOs/assetHistory';
import { AssetViewModel } from '@app/_models/_viewModels/asset.viewmodel';
import { AssetService } from '@app/_services';
import { DocumentService } from '@app/_services/document.service';
import { SelectionService } from '@app/_services/selection.service';
import { NzMessageService } from 'ng-zorro-antd';
import { Observable, Subject, Subscription } from 'rxjs';
import { AssetDetailsComponent } from '../asset-details/asset-details.component';
import { AssetUnassignmentComponent } from '../asset-unassignment/asset-unassignment.component';
import { AssetUnassignmentViewModel } from '@app/_models/_viewModels/assetUnassignment.viewmodel';

@Component({
    selector: 'app-asset-list',
    templateUrl: './asset-list.component.html',
    styleUrls: ['./asset-list.component.css']
})

export class AssetListComponent implements OnInit, ISelectionObserver {

    @Input() level: number;

    @Input() sorts: string;
    @Input() filters: string;
    @Input() pageNumber: number;
    @Input() pageSize: number;

    @ViewChild(AssetDetailsComponent, { static: false })
    private assetDetailsComponent: AssetDetailsComponent;

    assetsSubscription: Subscription;

    assetsTotal = 1;
    assetList: Asset[] = [];
    loadingAssets = true;

    isAllDisplayDataChecked = false;
    isOperating = false;
    isIndeterminate = false;
    mapOfCheckedId: { [key: string]: boolean } = {};
    numberOfChecked = 0;

    nameSearchValue = "";
    inventoryCodeSearchValue = "";
    departmentIdSearchValue: number[] = [];
    employeeIdSearchValue: number[] = [];
    locationIdSearchValue: number[] = [];
    assetTypeSearchValue: number[] = [];
    assetGroupSearchValue: number[] = [];
    supplierIdSearchValue: number[] = [];

    selectedSorts: Sort[] = [];

    public assetHistoryActionType = AssetHistoryActionType;
    public assetTypes = AssetType;

    assetViewModel: AssetViewModel = null;

    assetUnassignmentViewModel: AssetUnassignmentViewModel = {
        assetIds: [],
        departmentId: null,
        employeeId: null,
        locationId: null
    };
    assetUnassignmentIsVisible: boolean = false;
    isUnassignmentModalOpen: boolean = false;
    unassignmentSelectionIsOk: boolean = true;
    unassignmentAssetsLegalEntityId$ = new Subject<number>();

    constructor(
        private assetService: AssetService,
        private documentService: DocumentService,
        private messageService: NzMessageService,
        private selectionService: SelectionService
    ) {
    }
    selectionChangeSubscription: any;
    onSelectionChange(): void {

        if (this.selectionService.selectedAssets) {
            for (let key of Object.keys(this.mapOfCheckedId)) {
                if (this.selectionService.selectedAssets.some(x => x.id.toString() == key)) {
                    this.mapOfCheckedId[key] = true;
                }
                else {
                    this.mapOfCheckedId[key] = false;
                }
            }
            this.refreshStatus();
        }
    }

    ngOnInit() {
        this.assetService
            .getPage(this.pageNumber, this.pageSize, this.sorts, this.filters)
            .subscribe((data: ITuple<Asset>) => {
                this.loadingAssets = false;
                this.assetsTotal = data.item2;
                this.assetList = data.item1;
                this.initSelection();
            });
    }

    ngOnDestroy() {
        this.selectionChangeSubscription.unsubscribe();
    }

    initSelection() {
        if (this.selectionService.selectedAssets && this.selectionService.selectedAssets.length > 0) {
            for (let selectedAsset of this.selectionService.selectedAssets) {
                this.mapOfCheckedId[selectedAsset.id] = true;
            }
        }
        this.selectionChangeSubscription = this.selectionService.getSelectionChangeEmitter()
            .subscribe(x => this.onSelectionChange());

        this.refreshStatus();
    }

    searchData(reset: boolean = false): void {
        if (reset) {
            this.pageNumber = 1;
        }
        var filters = "";
        if (this.nameSearchValue.length > 0) {
            filters += "Name@=" + this.nameSearchValue + ",";
        }
        if (this.inventoryCodeSearchValue.length > 0) {
            filters +=
                "AssetInventoryNumber@=" + this.inventoryCodeSearchValue + ",";
        }
        if (this.departmentIdSearchValue.length > 0) {
            filters += "DepartmentId==" + this.departmentIdSearchValue.join("|") + ",";
        }
        if (this.employeeIdSearchValue.length > 0) {
            filters += "EmployeeId==" + this.employeeIdSearchValue.join("|") + ",";
        }
        if (this.locationIdSearchValue.length > 0) {
            filters += "LocationId==" + this.locationIdSearchValue.join("|") + ",";
        }
        if (this.assetTypeSearchValue.length > 0) {
            filters += "Type==" + this.assetTypeSearchValue.join("|") + ",";
        }
        if (this.assetGroupSearchValue.length > 0) {
            filters += "GroupId==" + this.assetGroupSearchValue.join("|") + ",";
        }
        if (this.supplierIdSearchValue.length > 0) {
            filters += "SupplierId==" + this.supplierIdSearchValue.join("|") + ",";
        }
        if (filters.length > 0) {
            filters = "," + filters;
        }
        this.loadingAssets = true;

        var sortString = this.getSortString();
        if (this.sorts && this.sorts.length > 0) {
            sortString = sortString + "," + this.sorts;
        }
        if (!sortString.includes("Created")) {
            if (sortString.length == 0) {
                sortString = "-Created"
            }
            else {
                sortString += ",-Created"
            }
        }

        this.assetService
            .getPage(this.pageNumber, this.pageSize, sortString, this.filters + filters)
            .subscribe((data: ITuple<Asset>) => {
                this.loadingAssets = false;
                this.assetsTotal = data.item2;
                this.assetList = data.item1;
            });
    }

    sort(sort: { key: string; value: string }): void {
        var newSort: Sort = {
            PropertyName: sort.key,
            Direction: null
        };
        if (sort.value) {
            if (sort.value == "ascend") {
                newSort.Direction = SortDirection.Ascending;
            }
            else {
                newSort.Direction = SortDirection.Descending;
            }
        }

        if (newSort.Direction == null) {
            var existingSort = this.selectedSorts.find(x => x.PropertyName == newSort.PropertyName);
            if (existingSort) {
                const index = this.selectedSorts.indexOf(existingSort);
                if (index > -1) {
                    this.selectedSorts.splice(index, 1);
                }
            }
        }
        else {
            var existingSort = this.selectedSorts.find(x => x.PropertyName == newSort.PropertyName);
            if (existingSort) {
                existingSort.Direction = newSort.Direction;
            }
            else {
                this.selectedSorts.push(newSort);
            }
        }
        this.searchData();
    }

    getSortString() {
        var sortString = "";
        if (this.selectedSorts && this.selectedSorts.length > 0) {
            for (let sort of this.selectedSorts) {
                if (sort.Direction == SortDirection.Ascending) {
                    if (sortString.length == 0) {
                        sortString = sort.PropertyName;
                    }
                    else {
                        sortString += "," + sort.PropertyName;
                    }
                }
                else {
                    if (sortString.length == 0) {
                        sortString = "-" + sort.PropertyName;
                    }
                    else {
                        sortString += ",-" + sort.PropertyName;
                    }
                }
            }
        }
        return sortString;
    }

    reset(): void {
        this.nameSearchValue = '';
        this.inventoryCodeSearchValue = '';
        this.departmentIdSearchValue = [];
        this.employeeIdSearchValue = [];
        this.locationIdSearchValue = [];
        this.assetTypeSearchValue = [];
        this.assetGroupSearchValue = [];
        this.supplierIdSearchValue = [];
        this.searchData(true);
    }

    openAssetDetails(asset: Asset) {
        this.assetDetailsComponent.loadAsset(asset.id);
    }

    openAssetDetailsFromId(assetId: number) {
        this.assetService
            .get(assetId)
            .subscribe((data: Asset) => {
                this.assetDetailsComponent.loadAsset(data.id);
            });
    }

    closeAssetDetails() {
        this.assetDetailsComponent.selectedAsset = null;
        this.assetDetailsComponent.isOpen = false;
    }

    refreshStatus(clickedId?: number): void {
        this.isAllDisplayDataChecked = this.assetList
            .every(item => this.mapOfCheckedId[item.id] == true) && Object.keys(this.mapOfCheckedId).length > 0;
        this.isIndeterminate =
            this.assetList.some(item => this.mapOfCheckedId[item.id]) && !this.isAllDisplayDataChecked;
        this.numberOfChecked = this.assetList.filter(item => this.mapOfCheckedId[item.id]).length;

        if (clickedId != null) {
            var clickedAsset = this.assetList.find(item => item.id == clickedId);
            if (this.mapOfCheckedId[clickedId]) {
                this.selectionService.addToSelection(clickedAsset);
            }
            else {
                this.selectionService.removeFromSelection(clickedAsset);
            }
        }
    }

    checkAll(value: boolean): void {
        this.assetList.forEach(item => (this.mapOfCheckedId[item.id] = value));

        if (value == true) {
            let selectedAssets = this.assetList.filter(item => this.mapOfCheckedId[item.id] == true);
            this.selectionService.addManyToSelection(selectedAssets);
        }
        else {
            let deselectedAssets = this.assetList.filter(item => this.mapOfCheckedId[item.id] == false);
            this.selectionService.removeManyFromSelection(deselectedAssets);
        }

        this.refreshStatus();
    }

    generateDocument() {
        var selectedAssets = this.assetList.filter(item => this.mapOfCheckedId[item.id]);
        this.documentService.generateDocument(selectedAssets).subscribe((result: any) => {
            this.messageService.create("success", "Dokument uspješno generiran");
            this.searchData();
            this.checkAll(false);

            if (result.type == 'application/pdf') {
                var blob = new Blob([result], { type: "application/pdf" });
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob);
                }
                else {
                    var fileUrl = URL.createObjectURL(blob);
                    window.open(fileUrl, '_blank');
                }
            }
        }, error => {

        });
    }

    checkUnassignmentSelection() {
        if (this.selectionService.selectedAssets && this.selectionService.selectedAssets.length > 0) {
            this.unassignmentSelectionIsOk = true;
            let firstSelectedAsset = this.selectionService.selectedAssets[0];
            if (firstSelectedAsset.employee) {
                if (!this.selectionService.selectedAssets.every(x => x.employee && x.employee.id == firstSelectedAsset.employee.id)) {
                    this.unassignmentSelectionIsOk = false;
                }
            }
            else if (firstSelectedAsset.location) {
                if (!this.selectionService.selectedAssets.every(x => x.location && x.location.id == firstSelectedAsset.location.id)) {
                    this.unassignmentSelectionIsOk = false;
                }
            }
        }
    }

    generateUnassignmentDocument() {
        this.checkUnassignmentSelection();
        if (this.unassignmentSelectionIsOk) {
            this.assetUnassignmentViewModel.assetIds = this.selectionService.selectedAssets.map(x => x.id);
            let firstSelectedAsset = this.selectionService.selectedAssets[0];
            if(this.selectionService.selectedAssets.every(x => x.department.legalEntity.id == firstSelectedAsset.department.legalEntity.id)) {
                this.unassignmentAssetsLegalEntityId$.next(firstSelectedAsset.department.legalEntity.id);
            }
            this.assetUnassignmentIsVisible = true;
        }
        else {
            this.isUnassignmentModalOpen = true;
        }
    }

    generateDocumentForSingle(asset: Asset) {
        var assetHistoryIds = [];
        var assets: Asset[] = [];
        assets.push(asset);
        this.documentService.generateDocument(assets).subscribe(result => {
            this.messageService.create("success", "Dokument uspješno generiran");
            this.searchData();
            this.checkAll(false);
        }, error => {

        });
    }

    public getActionTypeName(actionType: AssetHistoryActionType) {
        return AssetHistoryActionName.get(actionType).name;
    }

    public getAssetTypeName(assetType: AssetType) {
        return AssetTypeName.get(assetType);
    }

    public assetTypeKeys() {
        var keys = Object.keys(this.assetTypes);
        return keys.slice(keys.length / 2);
    }

    editAsset(asset: Asset) {
        this.assetViewModel = this.assetService.getAssetViewModel(asset);
    }

    closeEditDrawer(reload: boolean) {
        this.assetViewModel = null;
        if (reload) {
            this.searchData();
        }
    }

    closeUnasssignmentDrawer(reload: boolean) {
        this.assetUnassignmentViewModel = {
            assetIds: [],
            departmentId: null,
            employeeId: null,
            locationId: null
        };
        this.assetUnassignmentIsVisible = false;
        this.selectionService.clearSelection();
        if (reload) {
            this.searchData();
        }
    }

    closeUnasssignmentModal() {
        this.isUnassignmentModalOpen = false;
    }

    deselect(asset: Asset) {
        this.selectionService.removeFromSelection(asset, true);
        if (this.selectionService.selectedAssets.length == 0) {
            this.closeUnasssignmentModal();
        }
        this.checkUnassignmentSelection();
    }
}